import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';

// components
import { JxHint, JxTitleBar } from '../../../JxControls';

import '../../../JxControls/JxTheme.css';

// core
import umlCanvasEngine from '../../Core/UmlCanvasEngine.js';
import umlConnectorEngine from '../../Core/UmlConnectorEngine.js';
import { resolveInterfaceReduction } from '../../Core/resolveInterfaceReduction.js';
import { resolveUserHiddenItems } from '../../Core/resolveUserHiddenItems.js';

// local
// prettier-ignore
import {MAP_WIDTH, MAP_HEIGHT, MAP_PADDING, MIN_SHAPE_PX,  computeBounds, colorForShape, labelForShape, } from './UmlWorldMapHelpers.jsx';
import './UmlWorldMap.css';
import JxFormDraggableBase from '../../../JxControls/JxForms/JxFormDraggableBase.js';

/**
 * Floating minimap overview of the schema (VSCode minimap / Enterprise Architect
 * overview-style) — JxFormDraggableBase-based for z-index/position coordination.
 * See UmlWorldMap.txt for design rationale.
 */
export default class UmlWorldMap extends JxFormDraggableBase {
    constructor(props) {
        super(props);
        this.name = 'UmlWorldMap';
    }

    componentDidMount() {
        super.componentDidMount();
        this._unsubscribeCanvas = umlCanvasEngine.subscribe(() => this.forceUpdate());
    }

    componentWillUnmount() {
        this._unsubscribeCanvas?.();
        super.componentWillUnmount();
    }

    getDefaultGeo() {
        return { top: 10, left: 300, width: MAP_WIDTH, height: MAP_HEIGHT };
    }

    render() {
        const { shapes, viewConfig } = umlCanvasEngine;
        const connectors = umlConnectorEngine.connectors;

        // Same chaining as the real canvas (UmlCanvas.jsx) — otherwise the World Map would
        // keep showing shapes the user just hid, or interfaces Hide Interfaces filtered out
        // on the real canvas.
        const { visibleShapes: shapesAfterHidden, visibleConnectors: connectorsAfterHidden } = resolveUserHiddenItems(
            shapes,
            connectors,
            viewConfig,
        );
        const { visibleShapes } = resolveInterfaceReduction(shapesAfterHidden, connectorsAfterHidden, viewConfig);

        const drawableWidth = MAP_WIDTH - MAP_PADDING * 2;
        const drawableHeight = MAP_HEIGHT - MAP_PADDING * 2;
        const bounds = computeBounds(visibleShapes);
        const scale = bounds ? Math.min(drawableWidth / bounds.width, drawableHeight / bounds.height) : 1;

        return createPortal(
            <div
                {...this.panelProps}
                style={{ ...this.panelStyle, width: MAP_WIDTH }}
                className={`jx-panel uwm-map ${this.panelActiveClass}`}
            >
                <JxTitleBar
                    id="uwm_titlebar"
                    title="World Map"
                    isDraggable={true}
                    dragHandleProps={this.dragHandleProps}
                    actions={[{ icon: <CloseIcon sx={{ fontSize: 16 }} />, title: 'Close', onClick: this.props.onClose }]}
                    variant="toolwindow"
                />
                <div className="uwm-canvas" style={{ width: MAP_WIDTH, height: MAP_HEIGHT }}>
                    {!bounds && <span className="uwm-empty">Empty canvas</span>}
                    {bounds &&
                        visibleShapes.map(function renderShapeDot(shape) {
                            const left = (shape.x - bounds.minX) * scale + MAP_PADDING;
                            const top = (shape.y - bounds.minY) * scale + MAP_PADDING;
                            const width = Math.max(shape.w * scale, MIN_SHAPE_PX);
                            const height = Math.max(shape.h * scale, MIN_SHAPE_PX);
                            return (
                                <JxHint key={shape.id} title={labelForShape(shape)}>
                                    <div
                                        className="uwm-shape-dot"
                                        style={{ left, top, width, height, backgroundColor: colorForShape(shape) }}
                                    />
                                </JxHint>
                            );
                        })}
                </div>
            </div>,
            document.body,
        );
    }
}
